import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { ReactComponent as CoreLogo } from '../../assets/images/GH-GHX-Logos-Orange.svg';
import { ReactComponent as EnogenCoreLogo } from '../../assets/images/EnogenNKLogo.svg';
import { Dropdown, Tooltip, Modal, Badge, Select } from 'antd';
import { TabButton } from '../../components/TabButton/TabButton';
import { IconButton } from '../../components/IconButton/IconButton';
import { StyledModal } from '../../components/StyledModal/StyledModal';
import { UserSettingsContent } from './UserSettings/UserSettingsContent';
import { SoybeanTreatments } from './UserTreatments/SoybeanTreatments';
import { hasEffectivePermission, isManagingAccount, getCurrentActingUser, oktaLogout, clearStateForLogout, getManageableUsers } from '../../logic/store/User/AuthSlice';
import * as queryString from 'query-string';
import
{
	clearSelectedGrowerIdState,
	toggleDrawingTools,
	deleteDrawnFeatures,
	IInteractionMode,
	setMapInteractionMode,
	MapInteractionMode,
	setShowMapSearch,
	clearSelectedDashboardTab,
	clearSelectedYearState,
} from '../../logic/store/UI/UISlice';
import { startLogrocketRecording, clearState } from '../../logic/store/UI/RecordingSlice';
import { getProductAnalyzerLaunchUrl, redirectToGhxCoreSso, redirectToGhxLogisticsSso } from '../../logic/store/User/UserInfoSlice';
import { connect, ConnectedProps } from 'react-redux';
import { AppDispatch, persistor, RootState, useAppDispatch, useAppSelector } from '../../logic/store/Store';
import { UserAttributes } from './UserAttributes/UserAttributes';
import styled, { useTheme } from 'styled-components';
import { UserPermission } from '../../logic/Models/Responses/UserPermissions';
import { makeDispatch, sleepOnIt } from '../../logic/Utility/Utils';
import { DataHubMenu } from './DataHubMenu';
import { ItemType } from 'antd/lib/menu/hooks/useItems';
import { MenuProps } from 'antd/lib/menu';
import { clearState as clearFileState } from '../../logic/store/MachineFileData/FileSlice';
import { useAmplitudePassthrough, useNavigationEvent } from '../../logic/Utility/useAmplitude';
import { themeGet } from '@styled-system/theme-get';
import { useScopedSession } from '../../tracing/session';
import { EventStructureTags } from '../../tracing/EventTagNames';
import { EventNavigationNames } from '../../tracing/EventNames';
import { featureFlagSelector, Features, getFeatureFlag } from '../../logic/store/User/ConfigSlice';
import { PpgMain } from './Ppg/PpgMain';
import { clearState as ClearInsightsState } from '../../logic/store/Insights/InsightsSlice';
import { setManagedYear } from '../../logic/store/Seeds/CropsSlice';
import { useLocalStorage } from '../../hooks/useLocalStorage';

type MenuClickInfo = Parameters<NonNullable<MenuProps['onClick']>>[0];
type ClickableItem = ItemType & {
	onClick?: (info: MenuClickInfo) => void;
};

export interface IHeaderProps extends PropsFromRedux
{

}

const headerRoutes = {
	'/seeds': 'AcreOne',
	'/dashboard': 'Dashboard',
	'/fieldactivities': 'Field Activities',
	'/datahub': 'Data Hub',
	'/rootcause': 'Root Cause',
};
const pathSplit = /([/]\w+)/g;
const getSelectedPath = (pathname) =>
	pathname.split(pathSplit)
		.filter(part => part.length)
		.find(item => headerRoutes[item]);

const TitleMenuItem = styled.div`
	padding: 0;
	font-weight: ${themeGet('fontWeights.bold')};
	font-size: ${themeGet('fontSizes.normal')};
	line-height: ${themeGet('fontSizes.large')};
	color: ${themeGet('colors.darkGrey')};
`;

const SubTitleMenuItem = styled.div`
	font-weight: ${themeGet('fontWeights.bold')};
	font-size: ${themeGet('fontSizes.extraSmall')};
	line-height: ${themeGet('fontSizes.small')};
	color: ${themeGet('colors.mediumGrey')};
`;

export const HeaderComponent = (props: IHeaderProps) =>
{
	const {
		CanRecord,
		CanSeeTroubleShootingTools,
		CanSeeUserAdmin,
		Growers,
		HasGhxCoreSSO,
		IsLoadingGetTreatments,
		IsLoadingGrowers,
		IsLoadingSaveTreatments,
		IsLoadingUserAttributes,
		IsManagingAccount,
		IsRecording,
		IsSavingUserAttributes,
		SelectedAccount,
		UserInfo,
		UserSeesEnogenTheme,
		FeatureFlags,
		UserIsGrower,
		ManageableYearRecord,
		ManageableYearOptions,
		ChangeInteractionMode,
		ClearFileState,
		ClearRecordingState,
		ClearSelectedGrowerId,
		DeleteDrawn,
		DisplayDrawingTools,
		SetShowMapSearch,
		StartRecording,
		LaunchGhxCoreSso,
		ClearSelectedDashboardTab,
		ClearSelectedDashboardYear,
	} = props;

	const theme = useTheme();
	const location = useLocation();
	const history = useHistory();
	const dispatch = useAppDispatch();

	const [selectedTab, setSelectedTab] = useState<string>('/dashboard');
	const [areGrowersAvailable, setAreGrowersAvailable] = useState(!IsLoadingGrowers && Growers.length > 0);
	const [settingsModalVisible, setSettingsModalVisible] = useState(false);
	const [attributesModalVisible, setAttributesModalVisible] = useState(false);
	const [customTreatmentModalVisible, setCustomTreatmentModalVisible] = useState(false);
	const [ppgModalVisible, setPpgModalVisible] = useState(false);

	const dataHubMenu = DataHubMenu({openPpgModal: () => setPpgModalVisible(true), ...props});

	// Mobile header menu state
	const isMobile = useIsMobile(`(max-width: ${theme.breakpoints.mobile})`);
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

	const wrapMenuItemsToCloseMobile = (items: ItemType[] | undefined, closeMobile: () => void): ItemType[] | undefined =>
	{
		if (!items)
		{
			return items;
		}

		return items.map(item =>
		{
			if (!item)
			{
				return item;
			}

			// Only wrap items that actually have an onClick
			if ('onClick' in item && typeof item.onClick === 'function')
			{
				const clickableItem = item as ClickableItem;
				const originalOnClick = clickableItem.onClick;

				return {
					...clickableItem,
					onClick: (info: MenuClickInfo) =>
					{
						originalOnClick?.(info);
						closeMobile();
					}
				};
			}

			// Dividers, groups, submenu-only items, etc. pass through unchanged
			return item;
		});
	};

	const dataHubMenuMobile = useMemo(
		() => wrapMenuItemsToCloseMobile(dataHubMenu, () => setIsMobileMenuOpen(false)),
		[dataHubMenu, setIsMobileMenuOpen]
	);

	// AI Insights - AKA Root Cause
	const canSeeAiInsights = useAppSelector(state => hasEffectivePermission(state, UserPermission.CanSeeAiInsights, true));
	
	const session = useScopedSession(HeaderComponent.name, {
		[EventStructureTags.PageContext]: 'header',
		[EventStructureTags.PageUrl]: window.location.toString()
	});

	const selectTab = useCallback((tabName: string) =>
	{
		if (tabName === '/datahub')
		{
			// Only select the tab for datahub
			setSelectedTab(tabName);
			return;
		}

		if (!location.pathname.includes('rootcause'))
		{
			dispatch(ClearInsightsState()); // Only clear the Insights state when navigating away from Insights
		}
		
		ClearSelectedGrowerId();
		ClearFileState();
		setSelectedTab(tabName);
		DisplayDrawingTools(false);
		SetShowMapSearch(false);
		ChangeInteractionMode({ mode: MapInteractionMode.Selection });
		DeleteDrawn();
		ClearSelectedDashboardTab();
		ClearSelectedDashboardYear();
	}, [selectedTab, location.pathname]);

	const isOktaUser = useAppSelector(s => s.auth.userAuthToken === undefined);
	const performLogout = useAmplitudePassthrough(session, EventNavigationNames.LogOut, async () =>
	{
		let oktaLogoutUrl: string | undefined;
		if (isOktaUser)
		{
			try
			{
				oktaLogoutUrl = await dispatch(oktaLogout()).unwrap();
			}
			catch(e)
			{
				console.error('Encountered failure during okta signout. Ignoring.', e);
			}
		}

		dispatch(clearStateForLogout());

		await persistor.purge();

		// The Okta SSO session lives in a browser cookie, so we must navigate the browser to the
		// OIDC logout URL to actually terminate it. Okta then redirects back to PostLogoutRedirectUri
		// (/login). Without this the session survives and a different user on the same machine would
		// be silently logged in as the previous one. Fall back to a reload if no URL is returned.
		if (oktaLogoutUrl)
		{
			window.location.href = oktaLogoutUrl;
		}
		else
		{
			history.go(0);
		}
	}, []);

	useEffect(() => 
	{
		let path = getSelectedPath(location.pathname);

		// As a fallback, search the current URL for keywords
		if (!path)
		{
			if (location.pathname.includes('dashboard'))
			{
				path = '/dashboard';
			}
			else if (location.pathname.includes('fieldactivities') || location.pathname.includes('productgameplan'))
			{
				path = '/fieldactivities';
			}
		}

		setSelectedTab(path);
	}, [location.pathname]);

	useEffect(() =>
	{
		setAreGrowersAvailable(!IsLoadingGrowers && Growers.length > 0);
	}, [Growers]);

	// Get any relevant parameters from the url
	const parsed = queryString.parse(location.search);

	const trackStartRecording = useAmplitudePassthrough(session, EventNavigationNames.StartRecording, 
		() => StartRecording((parsed.cardid as string) ?? '1234'), [StartRecording, parsed.cardid]);

	
	const trackAccountInfo = useAmplitudePassthrough(session, EventNavigationNames.To, 
		() => setSettingsModalVisible(true), [], {
			[EventStructureTags.DestinationName]: 'account_info'
		});
	const trackCustomTreatments = useAmplitudePassthrough(session, EventNavigationNames.To, 
		() => setCustomTreatmentModalVisible(true), [], {
			[EventStructureTags.DestinationName]: 'custom_treatments'
		});
	const trackDefaultAttributes= useAmplitudePassthrough(session, EventNavigationNames.To, 
		() => setAttributesModalVisible(true), [], {
			[EventStructureTags.DestinationName]: 'default_attributes'
		});
	const trackSupport = useNavigationEvent(session, 'support', 'https://agconnections.zendesk.com/hc/en-us/categories/1500001012341-E-Luminate');
	const trackHelpfulLinks = useNavigationEvent(session, 'helpful_links', 'https://agconnections.zendesk.com/hc/en-us/categories/1500001012341-E-Luminate');

	const trackInsights = useAmplitudePassthrough(session, EventNavigationNames.To, 
		() => selectTab('/rootcause'), [], {
			[EventStructureTags.DestinationName]: 'rootcause'
		});
	const trackDashboard = useAmplitudePassthrough(session, EventNavigationNames.To, 
		() => selectTab('/dashboard'), [], {
			[EventStructureTags.DestinationName]: 'dashboard'
		});
	const trackSeeds = useAmplitudePassthrough(session, EventNavigationNames.To,
		() => window.open(`${process.env.REACT_APP_ACRE_ONE_URL}/acreone/`, '_blank'), [], {
			[EventStructureTags.DestinationName]: 'acreone'
		});
	const trackFieldActivities = useAmplitudePassthrough(session, EventNavigationNames.To, 
		() => selectTab('/fieldactivities'), [], {
			[EventStructureTags.DestinationName]: 'fieldactivities'
		});
	const trackDataHub = useAmplitudePassthrough(session, EventNavigationNames.To, 
		() => selectTab('/datahub'), [], {
			[EventStructureTags.DestinationName]: 'datahub'
		});

	// See if the url has requested a recording
	useEffect(() => 
	{
		// If there's a url request and we haven't already processed it
		if(parsed.record === 'true' && !IsRecording && UserInfo?.UserId)
		{
			// Remove the request from the url so we don't keep recording future sessions
			window.history.pushState({path:window.location.origin},'',window.location.origin);

			// Make sure the user acknowledges and confirms
			Modal.confirm({
				onOk: trackStartRecording,
				title: 'Consent to recording of your session',
				content: <div>
					Syngenta has shared this url that will record your session in order to assist with a support request.<br/>
					If you accept, all activity in this site will be recorded until you leave the site or reload the page.<br/>
					<br/>
					Do you consent to sharing your activity in this session of E-luminate?
				</div>
			});
		}
	}, [parsed.record, IsRecording, trackStartRecording, UserInfo?.UserId]);

	const settingsMenuItems: ItemType[] =
	[
		// If the user is managing an account, include the details here
		(IsManagingAccount && SelectedAccount) && {
			onClick: () => history.push('/dashboard'),
			key: 'Managed Account',
			style: { backgroundColor: theme.colors.secondary },
			label:
			<div>
				<TitleMenuItem>
					{SelectedAccount.UserName?.length > 0 ? SelectedAccount.UserName : SelectedAccount.UserEmail}
				</TitleMenuItem>
				<SubTitleMenuItem>
					{SelectedAccount.FoundationId}
				</SubTitleMenuItem>
			</div>
		},
		// If the user has the permission to record, or is already record (via for example a url) show
		// the record button
		(IsRecording || CanRecord) && {
			onClick: async () => 
			{
				if (IsRecording)
				{
					// Clear the recording state from the data store so it will not try to resume recording later 
					ClearRecordingState();

					// Give the datastore a quick moment to register clearing the recording data or it'll refresh with the previous data
					await sleepOnIt(50); 

					// Refresh the page to forcibly end the logrocket recording 
					window.location.reload();
				}
				else
				{
					StartRecording((parsed.cardid as string) ?? '1234');
				}
			},
			key: 'Record',
			className: 'HeaderProfileRecord',
			label: IsRecording ? <><span style={{color:'red'}}> &#11044; </span> End Recording</> : <>Record Session</>
		},
		{
			onClick: trackAccountInfo,
			key: 'Account Info',
			className: 'HeaderProfileAccountInfo',
			label: 'Account Info'
		},
		!UserIsGrower &&
		{
			onClick: trackCustomTreatments,
			key: 'Soybean Treatments',
			className: 'HeaderProfileCustomTreatments',
			label: 'Soybean Treatments'
		},
		{
			onClick: trackDefaultAttributes,
			key: 'Attribute Preferences',
			className: 'HeaderProfileAttributePreferences',
			label: 'Attribute Preferences'
		},
		{
			key: 'Contact Support',
			label:
			<Tooltip title="Send feedback or report issue" placement="left">
				<a
					onClick={() => trackSupport()}
					href='https://agconnections.zendesk.com/hc/en-us/categories/1500001012341-E-Luminate'
					rel='noreferrer'
					target='_blank'
					className='HeaderProfileContactSupport'>
					Contact Support
				</a>
			</Tooltip>
		},
		{
			key: 'Helpful Links',
			label:
			<Tooltip title="Review training videos and articles" placement="left">
				<a
					onClick={() => trackHelpfulLinks()}
					href='https://agconnections.zendesk.com/hc/en-us/categories/1500001012341-E-Luminate'
					rel='noreferrer'
					target='_blank'
					className='HeaderProfileHelpfulLinks'>
						Helpful Links
				</a>
			</Tooltip>
		},
		CanSeeTroubleShootingTools &&
		{
			key: 'TroubleShootingTools',
			className: 'HeaderProfileTroubleShootingTools',
			label: <Link to="/troubleshootingtools" key='TroubleShootingTools_Link'>Troubleshooting Tools</Link>
		},
		(getFeatureFlag(FeatureFlags, Features.InlineUserAdmin) && CanSeeUserAdmin) &&
		{
			key: 'UserAdmin',
			className: 'HeaderProfileTroubleShootingTools',
			label: <Link to="/useradmin" key='User Admin_Link'>User Administration</Link>
		},
		{
			key: 'Logout',
			onClick: performLogout,
			className: 'HeaderProfileLogout',
			label: 'Logout'
		}
	];

	const [selectedYear, setSelectedYear] = useLocalStorage<string>('selected-year', '');
	
	const handleSelectedYearOnChange = useCallback(
		(value: string, skipLoagingManageableUsers: boolean) =>
		{
			setSelectedYear(value);
			const selectedManageableYear = ManageableYearRecord[value];

			if(selectedManageableYear)
			{
				dispatch(setManagedYear({
					SalesYear: selectedManageableYear.SalesYear.toString(),
					HierarchyYear: selectedManageableYear.HierarchyYear.toString()
				}));
			}
			
			if(!skipLoagingManageableUsers)
			{
				dispatch(getManageableUsers());
			}
		}, [setSelectedYear, dispatch]);
			
	// Set the selected year to the first option on load if not already set
	useEffect(() =>
	{
		if(!selectedYear) 
		{
			const defaultSelectYear = ManageableYearOptions.length > 0 ? ManageableYearOptions[0].value : '';
			handleSelectedYearOnChange(defaultSelectYear, true);
		}
	},[ManageableYearOptions, selectedYear, handleSelectedYearOnChange]);

	return (
		<>
			<HeaderOuter id="header-container">
				<HeaderBar>
					<LogoWrapper>
						{
							UserSeesEnogenTheme ? (
								<EnogenCoreLogo
									width={'19vw'}
									height={'5.2vh'}
									style={{ minWidth: 235, minHeight: 45 }}
								/>
							) : (
								<CoreLogo
									width={'23vw'}
									height={'5.8vh'}
									style={{ minWidth: 235, minHeight: 45 }}
								/>
							)
						}
					</LogoWrapper>

					{/* DESKTOP NAV */}
					{!isMobile && (
						<DesktopNav id="desktop-nav">
							<Select 
								bordered
								aria-label="Sales Year"
								value={selectedYear}
								onChange={(value) => handleSelectedYearOnChange(value, false)}
								style={{
									alignSelf: 'center',
									border: '2px solid #121212',
									marginRight: 16,
									borderRadius: 4,
									fontWeight: 'bold',
								}}
							>
								{ManageableYearOptions.map(option => (
									<Select.Option key={option.value} value={option.value}>
										{option.label}
									</Select.Option>
								))}
							</Select>

							{canSeeAiInsights && (
								<Link
									to="/rootcause"
									style={{
										display: 'flex',
										justifyContent: 'center',
										color: theme.colors.darkGrey,
										height: '100%'
									}}
								>
									<TabButton
										className='HeaderButtonInsights'
										width={'100%'}
										style={{ margin: 0, height: '100%' }}
										selected={selectedTab === '/rootcause'}
										onClick={trackInsights}
									>
										{headerRoutes['/rootcause']}
									</TabButton>
								</Link>
							)}

							<Link
								to="/dashboard"
								style={{
									display: 'flex',
									justifyContent: 'center',
									color: theme.colors.darkGrey,
									height: '100%'
								}}
							>
								<TabButton
									className='HeaderButtonDashboard'
									width={'100%'}
									style={{ height: '100%' }}
									selected={selectedTab === '/dashboard'}
									onClick={trackDashboard}
								>
									{headerRoutes['/dashboard']}
								</TabButton>
							</Link>

							{HasGhxCoreSSO && (
								<TabButton
									className='HeaderButtonSeeds'
									style={{ height: '100%' }}
									selected={false}
									onClick={trackSeeds}
								>
									{headerRoutes['/seeds']}
								</TabButton>
							)}

							<Tooltip title={!areGrowersAvailable ? 'Select a Seed Advisor to advance.' : ''}>
								<Link
									to="/fieldactivities"
									style={{
										display: 'flex',
										justifyContent: 'center',
										color: theme.colors.darkGrey,
										height: '100%'
									}}
								>
									<TabButton
										className='HeaderButtonFieldActivities'
										width={'100%'}
										style={{ height: '100%' }}
										selected={selectedTab === '/fieldactivities'}
										onClick={trackFieldActivities}
										disabled={!areGrowersAvailable}
									>
										{headerRoutes['/fieldactivities']}
									</TabButton>
								</Link>
							</Tooltip>

							<Dropdown
								menu={{ items: dataHubMenu, style: { color: theme.colors.darkGrey, padding: 0 } }}
								trigger={['click']}
								placement='top'
							>
								<TabButton
									className='HeaderButtonDataHub'
									style={{ height: '100%' }}
									selected={selectedTab === '/datahub'}
									onClick={trackDataHub}
								>
									{headerRoutes['/datahub']}
								</TabButton>
							</Dropdown>

							<Dropdown
								menu={{
									items: settingsMenuItems,
									style: { color: theme.colors.darkGrey },
									className: 'HeaderProfileManagedAccount'
								}}
								trigger={['click']}
							>
								<Badge dot={IsRecording}>
									<IconButton
										className='HeaderButtonProfile'
										variant='profile'
										style={{
											marginLeft: 10,
											transform: 'translate(0px, -14px)',
											borderRadius: 16,
											backgroundColor: IsManagingAccount && theme.colors.secondary
										}}
									/>
								</Badge>
							</Dropdown>
						</DesktopNav>
					)}

					{/* MOBILE HEADER CONTROLS (hamburger + profile) */}
					{isMobile && (
						<>
							<Select 
								bordered
								aria-label="Sales Year"
								value={selectedYear} 
								onChange={(value) => handleSelectedYearOnChange(value, false)}
								style={{
									marginLeft: 'auto',
									marginRight: 10,
									alignSelf: 'center',
									border: '2px solid #121212',
									borderRadius: 4,
									fontWeight: 'bold',
								}}
							>
								{ManageableYearOptions.map(option => (
									<Select.Option key={option.value} value={option.value}>
										{option.label}
									</Select.Option>
								))}
							</Select>

							<HamburgerButton
								type="button"
								aria-label="Toggle navigation"
								onClick={() => setIsMobileMenuOpen(open => !open)}
							>
								<HamburgerIcon />
							</HamburgerButton>

							<MobileProfileWrapper>
								<Dropdown
									menu={{
										items: settingsMenuItems,
										style: { color: theme.colors.darkGrey },
										className: 'HeaderProfileManagedAccount'
									}}
									trigger={['click']}
								>
									<Badge dot={IsRecording}>
										<IconButton
											className='HeaderButtonProfile'
											variant='profile'
											style={{
												borderRadius: 16,
												backgroundColor: IsManagingAccount && theme.colors.secondary
											}}
										/>
									</Badge>
								</Dropdown>
							</MobileProfileWrapper>
						</>
					)}

				</HeaderBar>

				{/* MOBILE MENU CONTENT */}
				{isMobile && (
					<MobileMenu isOpen={isMobileMenuOpen}>
						{canSeeAiInsights && (
							<MobileMenuItem>
								<Link to="/rootcause" onClick={() => setIsMobileMenuOpen(false)}>
									<TabButton
										className='HeaderButtonInsights'
										width={'100%'}
										style={{ margin: 0, height: '100%' }}
										selected={selectedTab === '/rootcause'}
										onClick={trackInsights}
									>
										{headerRoutes['/rootcause']}
									</TabButton>
								</Link>
							</MobileMenuItem>
						)}

						<MobileMenuItem>
							<Link to="/dashboard" onClick={() => setIsMobileMenuOpen(false)}>
								<TabButton
									className='HeaderButtonDashboard'
									width={'100%'}
									style={{ margin: 0, height: '100%' }}
									selected={selectedTab === '/dashboard'}
									onClick={trackDashboard}
								>
									{headerRoutes['/dashboard']}
								</TabButton>
							</Link>
						</MobileMenuItem>

						{HasGhxCoreSSO && (
							<MobileMenuItem>
								<TabButton
									className='HeaderButtonSeeds'
									width={'100%'}
									style={{ margin: 0, height: '100%' }}
									selected={false}
									onClick={() => { setIsMobileMenuOpen(false); trackSeeds(); }}
								>
									{headerRoutes['/seeds']}
								</TabButton>
							</MobileMenuItem>
						)}

						<MobileMenuItem>
							<Tooltip title={!areGrowersAvailable ? 'Select a Seed Advisor to advance.' : ''}>
								<Link to="/fieldactivities" onClick={() => setIsMobileMenuOpen(false)}>
									<TabButton
										className='HeaderButtonFieldActivities'
										width={'100%'}
										style={{ margin: 0, height: '100%' }}
										selected={selectedTab === '/fieldactivities'}
										onClick={trackFieldActivities}
										disabled={!areGrowersAvailable}
									>
										{headerRoutes['/fieldactivities']}
									</TabButton>
								</Link>
							</Tooltip>
						</MobileMenuItem>

						<MobileMenuItem>
							<Dropdown
								menu={{
									items: dataHubMenuMobile,
									style: { color: theme.colors.darkGrey, padding: 0 }
								}}
								trigger={['click']}
								placement="bottomLeft"
							>
								<TabButton
									className='HeaderButtonDataHub'
									width={'100%'}
									style={{ margin: 0, height: '100%' }}
									selected={selectedTab === '/datahub'}
									onClick={trackDataHub}
								>
									{headerRoutes['/datahub']}
								</TabButton>
							</Dropdown>
						</MobileMenuItem>

					</MobileMenu>
				)}
			</HeaderOuter>

			<StyledModal
				open={attributesModalVisible}
				destroyOnClose={true}
				onCancel={() => setAttributesModalVisible(false)}
				width={'90%'}
				height={'85vh'}
				bodyStyle={{ overflow: 'hidden' }}
				isLoading={IsLoadingUserAttributes || IsSavingUserAttributes}
				loadingText={IsLoadingUserAttributes ? 'Loading' : IsSavingUserAttributes ? 'Saving' : ''}
			>
				<UserAttributes />
			</StyledModal>
			<StyledModal
				open={settingsModalVisible}
				onCancel={() => setSettingsModalVisible(false)}
			>
				<UserSettingsContent />
			</StyledModal>
			<StyledModal
				open={customTreatmentModalVisible}
				flexBody={true}
				loadingText={'Loading'}
				destroyOnClose={true}
				onCancel={() => setCustomTreatmentModalVisible(false)}
				isLoading={IsLoadingGetTreatments || IsLoadingSaveTreatments}
				width={'50%'}
				height={'75vh'}
			>
				<SoybeanTreatments />
			</StyledModal>
			<StyledModal
				open={ppgModalVisible}
				flexBody={true}
				loadingText={'Loading'}
				destroyOnClose={true}
				onCancel={() => setPpgModalVisible(false)}
				isLoading={IsLoadingGetTreatments || IsLoadingSaveTreatments}
				width={'55%'}
				height={'65vh'}
			>
				<PpgMain />
			</StyledModal>
		</>
	);

};

const mapStateToProps = (state: RootState) => ({
	CanRecord: hasEffectivePermission(state, UserPermission.CanRecord, true),
	CanSeeTroubleShootingTools: hasEffectivePermission(state, UserPermission.CanSeeTroubleShootingTools, true),
	CanSeeUserAdmin: hasEffectivePermission(state, UserPermission.UserAdministrator, true),
	Growers: state.grower.Growers,
	HasGhxCoreSSO: hasEffectivePermission(state, UserPermission.GhxCoreSSO, true),
	IsLoadingGetTreatments: state.customTreatments.isLoadingGetTreatments,
	IsLoadingGrowers: state.grower.isLoadingGrowers,
	IsLoadingSaveTreatments: state.customTreatments.isLoadingSaveTreatments,
	IsLoadingUserAttributes: state.userAttributes.isLoading,
	IsLoadingUserInfo: state.userInfo.isLoading,
	IsManagingAccount: isManagingAccount(state),
	IsRecording: state.recording.isRecording,
	IsSavingUserAttributes: state.userAttributes.isSaving,
	UserInfo: state.userInfo,
	UserIsGrower: hasEffectivePermission(state, UserPermission.IsGrower, true),
	UserSeesEnogenTheme: hasEffectivePermission(state, UserPermission.CanSeeEnogenTheme),
	SelectedAccount: getCurrentActingUser(state),
	FeatureFlags: featureFlagSelector(state),
	CanSsoLogistics: hasEffectivePermission(state, UserPermission.LogisticsSSO, true),
	UserCanSeePPG: hasEffectivePermission(state, UserPermission.CanSeePPG, true),
	ManageableYearRecord: state.crops.manageableYears,
	ManageableYearOptions: Object.entries(state.crops.manageableYears).map(([key, value]) => ({ label: value.SalesYear, value: key })),
	SelectedGrowerId: state.ui.SelectedGrowerId,
	CropYear: state.crops.CropYear,
	SellerFoundationId: (state.auth.selectedUser?.FoundationId ?? state.userInfo.FoundationId) as string,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
	ChangeInteractionMode: (mode: IInteractionMode) => dispatch(setMapInteractionMode(mode)),
	ClearFileState: makeDispatch(dispatch, clearFileState),
	ClearRecordingState: () => dispatch(clearState()),
	ClearSelectedGrowerId: () => dispatch(clearSelectedGrowerIdState()),
	DeleteDrawn: () => dispatch(deleteDrawnFeatures()),
	DisplayDrawingTools: (display: boolean) => dispatch(toggleDrawingTools(display)),
	LaunchProductAnalyzer: () => dispatch(getProductAnalyzerLaunchUrl()),
	SetShowMapSearch: ((value: boolean) => dispatch(setShowMapSearch(value))),
	StartRecording: (cardId: string) => dispatch(startLogrocketRecording(cardId)),
	RedirectToGhxLogistics: makeDispatch(dispatch, redirectToGhxLogisticsSso),
	LaunchGhxCoreSso: makeDispatch(dispatch, redirectToGhxCoreSso),
	ClearSelectedDashboardTab: makeDispatch(dispatch, clearSelectedDashboardTab),
	ClearSelectedDashboardYear: makeDispatch(dispatch, clearSelectedYearState),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export const Header = connector(HeaderComponent);

const useIsMobile = (query: string) =>
{
	const [matches, setMatches] = useState(() =>
		typeof window !== 'undefined'
			? window.matchMedia(query).matches
			: false
	);

	useEffect(() =>
	{
		if (typeof window === 'undefined')
		{
			return;
		}

		const mediaQuery = window.matchMedia(query);
		const handler = (e: MediaQueryListEvent) => setMatches(e.matches);

		// Ensure initial value is correct
		setMatches(mediaQuery.matches);

		mediaQuery.addEventListener('change', handler);
		return () => mediaQuery.removeEventListener('change', handler);
	}, [query]);

	return matches;
};


const HeaderOuter = styled.header`
	font-family: 'Roboto', sans-serif;
	font-weight: 700;
	font-size: 12px;
	height: 60px;
	box-shadow: 0px 4px 12px 0px rgba(0, 0, 0, 0.15);
	z-index: 999;
	position: relative;
`;

const HeaderBar = styled.section`
	background-color: ${({ theme }) => theme.colors.backgroundLM};
	color: ${({ theme }) => theme.colors.darkGrey};
	display: flex;
	align-items: center;
	height: 60px;
	padding: 0 60px;
	z-index: 998;
	position: relative;

	@media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
		padding: 0 16px;
	}
`;

const LogoWrapper = styled.div`
	display: flex;
	align-items: center;
`;

const DesktopNav = styled.div`
	display: flex;
	height: 100%;
	flex-grow: 1;
	align-items: flex-end;
	justify-content: flex-end;
	margin-left: auto;
`;

const HamburgerButton = styled.button`
	display: none;
	background: none;
	border: none;
	cursor: pointer;
	padding: 4px;

	@media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
		display: flex;
		align-items: center;
		justify-content: center;
	}
`;

const HamburgerIcon = styled.div`
	width: 20px;
	height: 2px;
	background: ${({ theme }) => theme.colors.darkGrey};
	position: relative;
	border-radius: 1px;

	&::before,
	&::after {
		content: '';
		position: absolute;
		left: 0;
		width: 20px;
		height: 2px;
		background: ${({ theme }) => theme.colors.darkGrey};
		border-radius: 1px;
	}

	&::before {
		top: -6px;
	}

	&::after {
		top: 6px;
	}
`;

// The slide-down mobile menu
const MobileMenu = styled.div<{ isOpen: boolean }>`
	position: absolute;
	top: 60px;
	left: 0;
	right: 0;
	background-color: ${({ theme }) => theme.colors.backgroundLM};
	box-shadow: 0px 4px 12px 0px rgba(0, 0, 0, 0.15);
	padding: 8px 16px 16px;
	display: flex;
	flex-direction: column;
	align-items: stretch;
	justify-content: flex-start;
	transition: opacity 0.15s ease, transform 0.15s ease;
	opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
	transform: ${({ isOpen }) => (isOpen ? 'translateY(0)' : 'translateY(-8px)')};
	pointer-events: ${({ isOpen }) => (isOpen ? 'auto' : 'none')};
	z-index: 997;

	@media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
		display: none;
	}
`;

const MobileMenuItem = styled.div`
	margin-top: 8px;

	a, button {
		width: 100% !important;
		max-width: 100% !important;
		display: flex;
		justify-content: center;
	}

	// Make TabButton fill width nicely
	.HeaderButtonDashboard,
	.HeaderButtonInsights,
	.HeaderButtonFieldActivities,
	.HeaderButtonSeeds,
	.HeaderButtonDataHub {
		width: 100% !important;
	}
`;

const MobileProfileWrapper = styled.div`
	display: none;

	@media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
		display: flex;
		align-items: center;
		justify-content: center;

		// Ensure transform from desktop is reset
		.HeaderButtonProfile {
			transform: none !important;
			margin-left: 12px;
		}
	}
`;
